import React, { memo, useCallback } from "react";
import { Avatar, IconButton, Tooltip } from "@toddle-edu/ds-web";
import classNames from "classnames";
import classes from "./StudentDetailsLayout.scss";
import { ChevronDownOutlined, ChevronUpOutlined } from "@toddle-edu/ds-icons";
import { useI18n } from "Hooks";
import SkeletonLoader from "UIComponents/SkeletonLoader";

const TEST_ID_PREFIX = "transcriptV3-studentDetailsLayout";

const StudentDetailsLayout = ({
  children,
  headerRightComponent,
  childrenClass,
  currentStudent,
  previousStudentId,
  nextStudentId,
  onStudentClick,
}) => {
  const { t } = useI18n();

  const handleNext = useCallback(() => {
    if (nextStudentId) onStudentClick(nextStudentId);
  }, [onStudentClick, nextStudentId]);

  const handlePrevious = useCallback(() => {
    if (previousStudentId) onStudentClick(previousStudentId);
  }, [onStudentClick, previousStudentId]);

  return (
    <main className={classes.content}>
      <div className={classes.header}>
        <div className={classes.headerLeft}>
          {currentStudent ? (
            <Avatar
              name={currentStudent.name}
              id={currentStudent.id}
              src={currentStudent.profileImage}
              size="medium"
            />
          ) : (
            <SkeletonLoader height="36px" width="36px" variant="circular" />
          )}
          {currentStudent ? (
            <div className={classes.headerText}>
              <Tooltip tooltip={currentStudent.name}>
                <h5 className="truncate">{currentStudent.name}</h5>
              </Tooltip>
              <Tooltip tooltip={currentStudent.sourceId}>
                <div
                  className={classNames("text-body truncate", classes.subtext)}
                >
                  {currentStudent.sourceId}
                </div>
              </Tooltip>
            </div>
          ) : (
            <SkeletonLoader height="48px" width="180px" />
          )}

          <div className={classes.studentSwitcher}>
            <Tooltip
              tooltip={t("common:previous_with_label_small", {
                label: t("common:student"),
              })}
            >
              <IconButton
                icon={<ChevronUpOutlined />}
                type="outlined"
                variant="neutral"
                size="x-small"
                onClick={handlePrevious}
                testId={`${TEST_ID_PREFIX}-previousButton`}
                shortcut={{
                  code: "KeyA",
                }}
                disabled={previousStudentId === currentStudent?.id}
              />
            </Tooltip>
            <Tooltip
              tooltip={t("common:next_with_label_small", {
                label: t("common:student"),
              })}
            >
              <IconButton
                icon={<ChevronDownOutlined />}
                type="outlined"
                variant="neutral"
                size="x-small"
                onClick={handleNext}
                testId={`${TEST_ID_PREFIX}-nextButton`}
                shortcut={{
                  code: "KeyD",
                }}
                disabled={nextStudentId === currentStudent?.id}
              />
            </Tooltip>
          </div>
        </div>
        {headerRightComponent}
      </div>
      <div className={classNames(classes.children, childrenClass)}>
        {children}
      </div>
    </main>
  );
};

export default memo(StudentDetailsLayout);
